import { useEffect, useState } from 'react';
import { Box, Grid, Modal, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useToaster } from '../../../../providers/ToasterProvider';
import { decideUsbApprovalRequest } from '../../../../services/usbApprovalRequestService';
import { describeUsbDevice, formatDateTime, seatOf } from './usbApprovalFormat';

const MAX_REMARK_LENGTH = 500;

const Field = ({ label, value, xs = 6 }) => (
    <Grid item xs={xs}>
        <Typography variant='body2' sx={{ color: 'grey.500' }}>{label}</Typography>
        <Typography variant='body1' sx={{ wordBreak: 'break-all' }}>{value || '-'}</Typography>
    </Grid>
);

// One request with its devices; approve or reject it with an optional remark.
export const UsbApprovalDecisionModal = ({ request, onClose, onDecisionComplete }) => {

    const { addNotification } = useToaster();
    const [remark, setRemark] = useState('');
    const [deciding, setDeciding] = useState(null);

    useEffect(() => { setRemark(''); }, [request]);

    const handleDecision = async (status) => {
        if (!request) return;
        setDeciding(status);
        try {
            const res = await decideUsbApprovalRequest(request.id, status, remark.trim() || undefined);
            if (res?.code === 'OK') {
                onDecisionComplete(request.id, status);
            }
        } catch (error) {
            addNotification(error?.message || `Unable to ${status === 'approved' ? 'approve' : 'reject'} the request.`, 'danger');
            console.log('There is an error while deciding a USB approval request', error);
        } finally {
            setDeciding(null);
        }
    }

    const isPending = request?.status === 'pending';

    return (
        <Modal open={Boolean(request)} hideBackdrop={true} className='backdrop' onClose={onClose}>
            <Box className='modal-container' sx={{ maxWidth: 800, p: 3 }}>
                <Typography variant='h5' component='h5' sx={{ mb: 2.5, color: 'primary.main' }}>
                    USB Approval Request
                </Typography>

                {request && <>
                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                        <Field label='Venue' value={request.venueName} />
                        <Field label='Lab / Seat' value={seatOf(request)} />
                        <Field label='Device ID' value={request.deviceId} xs={12} />
                        <Field label='MAC Address' value={request.macAddress} />
                        <Field label='Hostname' value={request.hostname} />
                        <Field label='Requested At' value={formatDateTime(request.requestedAt)} />
                        <Field label='Reason' value={request.reason} />
                        {!isPending && <>
                            <Field label={request.status === 'approved' ? 'Approved By' : 'Rejected By'} value={request.decidedByName ?? request.decidedBy} />
                            <Field label={request.status === 'approved' ? 'Approved At' : 'Rejected At'} value={formatDateTime(request.decidedAt)} />
                            <Field label='Remark' value={request.remark} xs={12} />
                        </>}
                    </Grid>

                    <Typography variant='subtitle2' sx={{ mb: 1, color: 'primary.main' }}>USB devices ({request.devices.length})</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                        {request.devices.map((device, index) => (
                            <Box key={index} sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                                <Typography variant='body1'>{describeUsbDevice(device)}</Typography>
                                <Typography variant='body2' sx={{ color: 'grey.500' }}>
                                    {`${device.vendorId}:${device.productId}`}{device.serial ? ` · Serial ${device.serial}` : ' · No serial'}{device.class ? ` · ${device.class}` : ''}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {isPending && <>
                        <TextField fullWidth multiline minRows={2} label='Remark (optional)' value={remark}
                            onChange={(event) => setRemark(event.target.value)} inputProps={{ maxLength: MAX_REMARK_LENGTH }} />
                        <Box className='actions-container' sx={{ mt: 3, justifyContent: 'center', gap: '40px' }}>
                            <LoadingButton sx={{ borderRadius: 40 }} color='secondary' variant='contained'
                                onClick={() => handleDecision('rejected')} loading={deciding === 'rejected'} disabled={Boolean(deciding)}>
                                Reject
                            </LoadingButton>
                            <LoadingButton sx={{ borderRadius: 40 }} color='secondary' variant='contained'
                                onClick={() => handleDecision('approved')} loading={deciding === 'approved'} disabled={Boolean(deciding)}>
                                Approve
                            </LoadingButton>
                        </Box>
                    </>}
                </>}
            </Box>
        </Modal>
    );
}
